import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

// Query keys for consistent caching
export const queryKeys = {
  auth: ["auth"],
  bots: ["bots"],
  bot: (id) => ["bots", id],
  sources: (botId) => ["bots", botId, "sources"],
  source: (botId, sourceId) => ["bots", botId, "sources", sourceId],
  widgetEmbed: (botId) => ["bots", botId, "widget-embed"],
  conversations: (botId) => ["conversations", botId],
  conversation: (botId, sessionId) => ["conversations", botId, sessionId],
  conversationStats: (botId) => ["conversations", botId, "stats"],
  analytics: (botId) => ["analytics", botId],
  analyticsTimeseries: (botId, granularity) => ["analytics", botId, "timeseries", granularity],
  topCitations: (botId) => ["analytics", botId, "top-citations"],
  topCards: (botId) => ["analytics", botId, "top-cards"],
  tenantAnalytics: ["analytics", "tenant"],
  contacts: (botId) => ["contacts", botId],
  // Sotto il prefisso dei contatti: le invalidazioni dopo cambio di stato e
  // cancellazione raggiungono anche il singolo contatto aperto.
  contact: (botId, contactId) => ["contacts", botId, "contact", contactId],
  contactStats: (botId) => ["contacts", botId, "stats"],
  contactForm: (botId) => ["contacts", botId, "form"],
  integrations: (botId) => ["bots", botId, "integrations"],
};

// Authentication hooks
export const useAuthCheck = () => {
  return useQuery({
    queryKey: queryKeys.auth,
    queryFn: api.auth.check,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear();
      // Redirect to login (handled by auth context)
    },
    onError: (error) => {
      console.error("Logout failed:", error);
      // Clear cache even if logout fails
      queryClient.clear();
    },
  });
};

export const useRefreshAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.auth.refresh,
    onSuccess: () => {
      // Invalidate auth query to refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth });
    },
    onError: (error) => {
      console.error("Auth refresh failed:", error);
      // Clear auth data on refresh failure
      queryClient.removeQueries({ queryKey: queryKeys.auth });
    },
  });
};

// Bot management hooks
export const useBots = () => {
  return useQuery({
    queryKey: queryKeys.bots,
    queryFn: api.bots.list,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useBot = (id) => {
  return useQuery({
    queryKey: queryKeys.bot(id),
    queryFn: () => api.bots.get(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateBot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.bots.create,
    // Gli errori di creazione sono deterministici (es. 402 limite piano):
    // il retry globale ritarda solo il messaggio all'utente.
    retry: false,
    onSuccess: () => {
      // Invalidate bots list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.bots });
    },
  });
};

export const useAnalyzeWebsite = () => {
  return useMutation({
    mutationFn: (websiteUrl) => api.bots.analyzeWebsite(websiteUrl),
  });
};

export const useUpdateBot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => api.bots.update(id, data),
    onSuccess: (response, variables) => {
      // Extract bot from response (API returns { bot: {...}, message: "..." })
      const updatedBot = response.bot || response;
      
      // Update the specific bot in cache
      queryClient.setQueryData(queryKeys.bot(variables.id), { bot: updatedBot });
      
      // Invalidate bots list to refetch with updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.bots });
    },
  });
};

export const useDeleteBot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.bots.delete,
    onSuccess: (data, botId) => {
      // Remove bot from cache
      queryClient.removeQueries({ queryKey: queryKeys.bot(botId) });
      // Invalidate bots list
      queryClient.invalidateQueries({ queryKey: queryKeys.bots });
    },
  });
};

// Source management hooks
export const useSources = (botId, params = {}) => {
  return useQuery({
    // Prefix with queryKeys.sources(botId) so mutation invalidations
    // (invalidateQueries({ queryKey: queryKeys.sources(botId) })) MATCH this query
    // and trigger a refetch; the params make page/search/type/status refetch too.
    queryKey: [
      ...queryKeys.sources(botId),
      params.page,
      params.limit,
      params.search,
      params.type,
      params.status,
    ],
    queryFn: () => api.sources.list(botId, params),
    enabled: !!botId,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev, // keep previous page visible during fetch (rq v5)
  });
};

// For compatibility - all sources without pagination
export const useAllSources = (botId) => {
  return useQuery({
    queryKey: queryKeys.sources(botId),
    queryFn: () => api.sources.listAll(botId),
    enabled: !!botId,
    staleTime: 1 * 60 * 1000,
    // 1 minute
  });
};

// Sitemap children with pagination
export const useSitemapChildren = (botId, sitemapId, params = {}) => {
  return useQuery({
    queryKey: ['sitemapChildren', botId, sitemapId, params],
    queryFn: () => api.sources.getSitemapChildren(botId, sitemapId, params),
    enabled: !!(botId && sitemapId),
    staleTime: 1 * 60 * 1000,
  });
};

// Sitemap stats
export const useSitemapStats = (botId, sitemapId) => {
  return useQuery({
    queryKey: ['sitemapStats', botId, sitemapId],
    queryFn: () => api.sources.getSitemapStats(botId, sitemapId),
    enabled: !!(botId && sitemapId),
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useSource = (botId, sourceId) => {
  return useQuery({
    queryKey: queryKeys.source(botId, sourceId),
    queryFn: () => api.sources.get(botId, sourceId),
    enabled: !!(botId && sourceId),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useCreateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, data }) => api.sources.create(botId, data),
    onSuccess: (data, variables) => {
      // Invalidate sources list for the bot
      queryClient.invalidateQueries({
        queryKey: queryKeys.sources(variables.botId),
      });
    },
  });
};

export const useBulkCreateSources = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, sources }) => api.sources.bulkCreate(botId, sources),
    onSuccess: (data, variables) => {
      // Invalidate sources list for the bot
      queryClient.invalidateQueries({
        queryKey: queryKeys.sources(variables.botId),
      });
    },
  });
};

// Remove a deleted source from a cached sources query. Handles both the
// paginated shape `{ sources: [...], pagination }` and a plain array.
const stripSourceFromCache = (old, sourceId) => {
  if (!old) return old;
  if (Array.isArray(old)) return old.filter((s) => s?._id !== sourceId);
  if (Array.isArray(old.sources))
    return { ...old, sources: old.sources.filter((s) => s?._id !== sourceId) };
  return old;
};

export const useDeleteSource = () => {
  const queryClient = useQueryClient();

  // The displayed (paginated) list is cached under ["sources", botId, params],
  // while queryKeys.sources(botId) is ["bots", botId, "sources"] (the listAll).
  // We touch BOTH so the row vanishes immediately wherever it's shown.
  const listKey = (botId) => ["sources", botId];

  return useMutation({
    mutationFn: ({ botId, sourceId }) => api.sources.delete(sourceId),
    // Optimistic update: drop the row right away, no wait for the refetch.
    onMutate: async ({ botId, sourceId }) => {
      await queryClient.cancelQueries({ queryKey: listKey(botId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.sources(botId) });
      const previous = [
        ...queryClient.getQueriesData({ queryKey: listKey(botId) }),
        ...queryClient.getQueriesData({ queryKey: queryKeys.sources(botId) }),
      ];
      queryClient.setQueriesData({ queryKey: listKey(botId) }, (old) =>
        stripSourceFromCache(old, sourceId)
      );
      queryClient.setQueriesData({ queryKey: queryKeys.sources(botId) }, (old) =>
        stripSourceFromCache(old, sourceId)
      );
      return { previous };
    },
    // If the delete fails, put the row back.
    onError: (err, variables, context) => {
      context?.previous?.forEach(([key, data]) =>
        queryClient.setQueryData(key, data)
      );
    },
    // Re-sync with the server either way, using keys that actually match.
    onSettled: (data, error, { botId, sourceId }) => {
      queryClient.removeQueries({ queryKey: queryKeys.source(botId, sourceId) });
      queryClient.invalidateQueries({ queryKey: listKey(botId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.sources(botId) });
    },
  });
};

export const useReprocessSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, sourceId }) => api.sources.reingest(sourceId),
    onSuccess: (data, variables) => {
      // Invalidate source to refetch updated status
      queryClient.invalidateQueries({
        queryKey: queryKeys.source(variables.botId, variables.sourceId),
      });
      // Invalidate sources list
      queryClient.invalidateQueries({
        queryKey: queryKeys.sources(variables.botId),
      });
    },
  });
};

export const useUpdateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, sourceId, data }) =>
      api.sources.update(sourceId, data),
    onSuccess: (data, variables) => {
      // Invalidate source to refetch updated status/content
      queryClient.invalidateQueries({
        queryKey: queryKeys.source(variables.botId, variables.sourceId),
      });
      // Invalidate sources list so the row reflects the edit
      queryClient.invalidateQueries({
        queryKey: queryKeys.sources(variables.botId),
      });
    },
  });
};

// Upload hooks
export const useGetSasUrl = () => {
  return useMutation({
    mutationFn: ({ filename, contentType, botId }) =>
      api.uploads.getSasUrl(filename, contentType, botId),
  });
};

// Widget management hooks
export const useUpdateWidgetConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, widgetConfig }) => api.bots.updateWidgetConfig(botId, widgetConfig),
    onSuccess: (data, variables) => {
      // Invalidate widget embed cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.widgetEmbed(variables.botId),
      });
      // Also invalidate bot details
      queryClient.invalidateQueries({
        queryKey: queryKeys.bot(variables.botId),
      });
    },
  });
};

export const useWidgetEmbed = (botId) => {
  return useQuery({
    queryKey: queryKeys.widgetEmbed(botId),
    queryFn: async () => {
      console.log('Fetching widget embed for botId:', botId);
      const result = await api.bots.getWidgetEmbed(botId);
      console.log('Widget embed result:', result);
      return result;
    },
    enabled: !!botId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Query hooks
export const useAskQuestion = (botId) => {
  return useMutation({
    mutationFn: ({ question }) => api.query.ask(botId, question),
  });
};

// Conversation hooks
// Due forme: useConversations(botId, { page, limit, q, conContatto }) e quella
// storica useConversations(botId, page, limit), che resta valida.
export const useConversations = (botId, pageOrOptions = 1, limitArg = 100) => {
  const options =
    pageOrOptions && typeof pageOrOptions === "object" ? pageOrOptions : { page: pageOrOptions, limit: limitArg };
  const { page = 1, limit = 100, q, conContatto = false, status = "" } = options;
  // "  mario " e "mario" sono la stessa ricerca: una sola voce in cache.
  const search = typeof q === "string" ? q.trim() : "";
  const withContact = !!conContatto;
  return useQuery({
    queryKey: [...queryKeys.conversations(botId), page, limit, search, withContact, status],
    queryFn: () =>
      api.conversations.list(botId, {
        page,
        limit,
        ...(search ? { q: search } : {}),
        ...(withContact ? { con_contatto: 1 } : {}),
        ...(status ? { status } : {}),
      }),
    enabled: !!botId,
    staleTime: 1 * 60 * 1000, // 1 minute
    // Cambiando pagina o ricerca resta visibile l'elenco precedente (in rq v5
    // keepPreviousData non esiste piu'), ma mai quello di un altro bot.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey?.[1] === botId ? prev : undefined),
  });
};

export const useConversation = (botId, sessionId) => {
  return useQuery({
    queryKey: queryKeys.conversation(botId, sessionId),
    queryFn: () => api.conversations.get(botId, sessionId),
    enabled: !!botId && !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useConversationStats = (botId) => {
  return useQuery({
    queryKey: queryKeys.conversationStats(botId),
    queryFn: () => api.conversations.getStats(botId),
    enabled: !!botId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Da leggere / letta / archiviata. Elenco, dettaglio e conteggi hanno lo
// stesso prefisso: si rileggono tutti.
export const useSetConversationStatus = (botId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, status }) => api.conversations.setStatus(botId, sessionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations(botId) });
    },
  });
};

export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ botId, sessionId }) => api.conversations.delete(botId, sessionId),
    onSuccess: (data, variables) => {
      // Invalidate conversations list
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations(variables.botId),
      });
      // Invalidate conversation stats
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversationStats(variables.botId),
      });
      // Remove the specific conversation from cache
      queryClient.removeQueries({
        queryKey: queryKeys.conversation(variables.botId, variables.sessionId),
      });
    },
  });
};

// Analytics hooks
export const useBotAnalytics = (botId, startDate, endDate) => {
  return useQuery({
    queryKey: [...queryKeys.analytics(botId), startDate, endDate],
    queryFn: () => api.analytics.getBot(botId, { startDate, endDate }),
    enabled: !!botId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useBotTimeseriesAnalytics = (botId, granularity, startDate, endDate) => {
  return useQuery({
    queryKey: [...queryKeys.analyticsTimeseries(botId, granularity), startDate, endDate],
    queryFn: () => api.analytics.getBotTimeseries(botId, { granularity, startDate, endDate }),
    enabled: !!botId && !!granularity,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTenantAnalytics = (startDate, endDate) => {
  return useQuery({
    queryKey: [...queryKeys.tenantAnalytics, startDate, endDate],
    queryFn: () => api.analytics.getTenant({ startDate, endDate }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTopCitations = (botId, startDate, endDate, limit = 20) => {
  return useQuery({
    queryKey: [...queryKeys.topCitations(botId), startDate, endDate, limit],
    queryFn: () => api.analytics.getTopCitations(botId, { startDate, endDate, limit }),
    enabled: !!botId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTopCards = (botId, startDate, endDate, limit = 20) => {
  return useQuery({
    queryKey: [...queryKeys.topCards(botId), startDate, endDate, limit],
    queryFn: () => api.analytics.getTopCards(botId, { startDate, endDate, limit }),
    enabled: !!botId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ---------------------------------------------------------------------------
// Contatti raccolti dal modulo in chat
// ---------------------------------------------------------------------------

export const useContacts = (botId, { page = 1, limit = 25, status, q } = {}) => {
  // "  mario " e "mario" sono la stessa ricerca: una sola voce in cache.
  const search = typeof q === "string" ? q.trim() : "";
  return useQuery({
    queryKey: [...queryKeys.contacts(botId), page, limit, status || "all", search],
    queryFn: () =>
      api.contacts.list(botId, { page, limit, ...(status ? { status } : {}), ...(search ? { q: search } : {}) }),
    enabled: !!botId,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev, // resta visibile la pagina precedente durante il caricamento (rq v5)
  });
};

export const useContact = (botId, contactId) => {
  return useQuery({
    queryKey: queryKeys.contact(botId, contactId),
    queryFn: () => api.contacts.get(botId, contactId),
    enabled: !!botId && !!contactId,
    staleTime: 30 * 1000,
    // Un contatto cancellato (o un link vecchio) risponde 404: riprovare
    // ritarderebbe solo il messaggio "non trovato".
    retry: (failureCount, error) => error?.status !== 404 && failureCount < 2,
  });
};

export const useContactStats = (botId) => {
  return useQuery({
    queryKey: queryKeys.contactStats(botId),
    queryFn: () => api.contacts.getStats(botId),
    enabled: !!botId,
    staleTime: 30 * 1000,
  });
};

export const useContactForm = (botId) => {
  return useQuery({
    queryKey: queryKeys.contactForm(botId),
    queryFn: () => api.contacts.getForm(botId),
    enabled: !!botId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSaveContactForm = (botId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (definition) => api.contacts.saveForm(botId, definition),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contactForm(botId) });
      // Accendere o spegnere il modulo cambia anche la sua card nelle integrazioni.
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations(botId) });
    },
  });
};

// ---------------------------------------------------------------------------
// Apps & Integrazioni
// ---------------------------------------------------------------------------

export const useIntegrations = (botId) => {
  return useQuery({
    queryKey: queryKeys.integrations(botId),
    queryFn: () => api.integrations.get(botId),
    enabled: !!botId,
    staleTime: 60 * 1000,
  });
};

export const useSaveIntegrations = (botId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes) => api.integrations.save(botId, changes),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.integrations(botId), data);
      // L'interruttore del modulo vive anche nel compositore dei contatti.
      queryClient.invalidateQueries({ queryKey: queryKeys.contactForm(botId) });
    },
  });
};

export const useSetContactStatus = (botId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, status }) => api.contacts.setStatus(botId, contactId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts(botId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contactStats(botId) });
    },
  });
};

export const useDeleteContact = (botId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId) => api.contacts.delete(botId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts(botId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contactStats(botId) });
      // Le conversazioni mostrano il loro contatto (elenco e dettaglio, stesso
      // prefisso): cancellato il contatto, vanno rilette.
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations(botId) });
    },
  });
};

// Abbonamento: l'account (piano, stato dei pagamenti), il catalogo dei
// piani, i consumi e i dati di fatturazione. Il piano lo cambiano solo Stripe
// (webhook) e gli admin: da qui si legge.
export const billingKeys = {
  account: ["billing", "account"],
  catalog: ["billing", "catalog"],
  usage: ["billing", "usage"],
  profile: ["billing", "profile"],
};

export const useBillingAccount = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: billingKeys.account,
    queryFn: () => api.billing.getAccount(),
    enabled,
    retry: false,
  });

export const useBillingCatalog = () =>
  useQuery({ queryKey: billingKeys.catalog, queryFn: () => api.billing.listPlans(), staleTime: 5 * 60 * 1000 });

export const useBillingUsage = () =>
  useQuery({ queryKey: billingKeys.usage, queryFn: () => api.billing.getSubscription() });

export const useBillingProfile = ({ enabled = true } = {}) =>
  useQuery({ queryKey: billingKeys.profile, queryFn: () => api.billing.getProfile(), enabled, retry: false });

export const useSaveBillingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billing) => api.billing.updateProfile(billing),
    onSuccess: (data) => {
      queryClient.setQueryData(billingKeys.profile, (vecchio) => ({
        ...(vecchio || {}),
        billing: data.billing,
        completo: data.completo,
        mancanti: data.mancanti,
        errori: {},
      }));
      queryClient.invalidateQueries({ queryKey: billingKeys.account });
    },
  });
};
